import React, { useState, useEffect, useRef } from "react"
import axios from "axios"
import { useNavigate, useParams, Link } from "react-router-dom"
import { FaAngleLeft, FaAngleRight, FaPlus } from 'react-icons/fa'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import { apiURL, filesURL } from 'config/globals'
import Topbar from 'components/Topbar'
import CraftRecordNavigation from 'components/CraftRecordNavigation'
import FormRow from 'components/FormRow'
import { checkAuth } from 'utils/Auth'
import { Typeahead } from 'react-bootstrap-typeahead'
import ReactImagePickerEditor from 'react-image-picker-editor'
import 'react-image-picker-editor/dist/index.css'
import StickyFooter from "components/StickyFooter"

const CraftRecord6 = () => {
    const [currentUser, setCurrentUser] = useState()
    const [museum, setMuseum] = useState({})
    const [manufacturingTags, setManufacturingTags] = useState([])
    const [decorationTags, setDecorationTags] = useState([])
    const [craftRecord, setCraftRecord] = useState({
        type: 'traditional'
    })
    const [form, setForm] = useState({})
    const [loadError, setLoadError] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState('')
    const [manufacturingPhases, setManufacturingPhases] = useState([])

    const navigate = useNavigate()
    const { entityId, craftRecordId } = useParams()
    const manufacturingTechniquesTagsRef = useRef(null)
    const decorationTechniquesTagsRef = useRef(null)
    
    useEffect(() => {
        const start = async () => {
            setCurrentUser(await checkAuth(navigate))
            await getMuseum(entityId)
            if (craftRecordId) {
                await getCraftRecord(craftRecordId)
            }
            await getManufacturingTags()
            await getDecorationTags()
        }
        start()
    }, [])

    const getMuseum = async (id) => {
        try {
            const response = await axios.get(`${apiURL}/get-museum/${id}`)

            if (!response.data.success) {
                setLoadError("Cannot load museum data: " + (response.data.error || response.data))
                return
            }

            setMuseum(response.data.item)
        } catch (error) {
            setLoadError(JSON.stringify(error))
        }
    }

    const getManufacturingTags = async () => {
        try {
            const response = await axios.get(`${apiURL}/get-table-data/craft_manufacturing_tags`)

            if (!response.data.success) {
                setLoadError("Cannot load craft manufacturing tags: " + (response.data.error || response.data))
                return
            }

            setManufacturingTags(response.data.rows.map(x => x.label))
        } catch (error) {
            setLoadError(JSON.stringify(error))
        }
    }

    const getDecorationTags = async () => {
        try {
            const response = await axios.get(`${apiURL}/get-table-data/craft_decoration_tags`)

            if (!response.data.success) {
                setLoadError("Cannot load craft decoration tags: " + (response.data.error || response.data))
                return
            }

            setDecorationTags(response.data.rows.map(x => x.label))
        } catch (error) {
            setLoadError(JSON.stringify(error))
        }
    }

    const getCraftRecord = async (id) => {
        try {
            const response = await axios.get(`${apiURL}/get-craft-record/${id}`)

            if (!response.data.success) {
                setLoadError("Cannot load record data: " + (response.data.error || response.data))
                return
            }

            setCraftRecord(response.data.item)
            if (response.data.item.manufacturingPhases) {
                setManufacturingPhases(JSON.parse(response.data.item.manufacturingPhases))
            }
        } catch (error) {
            setLoadError(JSON.stringify(error))
        }
    }

    const handleChange = (field, value) => {
        setForm({
            ...form,
            [field]: value
        })
    }

    const save = async (dataUri) => {
        setSubmitError('')
        setIsSubmitting(true)

        const submittedForm = {...form}
        if (dataUri) {
            submittedForm.decorationTechniquesImage = dataUri
        }
        
        try {
            const response = await axios.put(`${apiURL}/update-craft-record/${craftRecord.id}`, submittedForm)
            
            if (!response.data.success) {
                setSubmitError(response.data.error || response.data)
                setIsSubmitting(false)
                return
            }

            setIsSubmitting(false)
        } catch (error) {
            setSubmitError(JSON.stringify(error))
            setIsSubmitting(false)
        }
    }

    const previousPage = () => {
        navigate(`/museum/${museum.id}/documentation/craft-records/${craftRecord.id}/edit-materials`)
    }

    const nextPage = () => {
        navigate(`/museum/${museum.id}/documentation/craft-records/${craftRecord.id}/edit-product`)
    }

    const handleChangePhase = (index, value) => {
        const phases = manufacturingPhases
        phases[index] = value
        setManufacturingPhases(phases)
        handleChange('manufacturingPhases', phases)
    }

    const addManufacturingPhase = () => {
        const phases = manufacturingPhases.concat("")
        setManufacturingPhases(phases)
        handleChange('manufacturingPhases', phases)
        save()
    }

    const removeManufacturingPhase = () => {
        const phases = manufacturingPhases.slice(0, -1)
        setManufacturingPhases(phases)
        handleChange('manufacturingPhases', phases)
        save()
    }

    return (
        <>
            <Topbar currentUser={currentUser} />
            <main className="full-width" style={{paddingBottom: 300, height: 'fit-content'}}>
                {museum.id &&
                    <>
                        <div>
                            <Link to={`/museum/${museum.id}/documentation/craft-records`} className="no-decoration"><FaAngleLeft /> Back to Craft Records</Link>
                        </div>
                        <header className="mt-2">
                            <div className="float-start">
                                <h1>Craft Record</h1>
                            </div>
                        </header>
                        <CraftRecordNavigation currentTab="process" museumId={museum.id} craftRecordId={craftRecord.id} />
                        {(!craftRecordId || craftRecord.id) &&
                            <>
                                <h3>Manufacturing techniques</h3>
                                <FormRow
                                    key="manufacturingTechniquesTags"
                                    label="General tags"
                                    content={
                                        <Typeahead
                                            multiple
                                            allowNew
                                            id="manufacturingTechniquesTags"
                                            onChange={selected => {
                                                handleChange('manufacturingTechniquesTags', selected.map(x => x.label ? x.label : x))
                                                manufacturingTechniquesTagsRef.current.toggleMenu()
                                            }}
                                            onBlur={save}
                                            options={manufacturingTags}
                                            placeholder="Select general manufacturing techniques"
                                            ref={manufacturingTechniquesTagsRef}
                                            defaultSelected={craftRecord.manufacturingTechniquesTags
                                                ? craftRecord.manufacturingTechniquesTags.split(", ")
                                                : []
                                            }
                                        />
                                    }
                                />
                                <FormRow
                                    key="manufacturingTechniquesDesc"
                                    label="Specific techniques"
                                    content={
                                        <Form.Control
                                            name="manufacturingTechniquesDesc"
                                            placeholder="Describe the specific techniques related to the manufacturing of the craft activity"
                                            defaultValue={craftRecord.manufacturingTechniquesDesc}
                                            as="textarea"
                                            rows="5"
                                            onChange={e => handleChange(e.target.name, e.target.value)}
                                            onBlur={save}
                                        />
                                    }
                                />
                                <h3>Decoration</h3>
                                <FormRow
                                    key="decorationTechniquesTags"
                                    label="General tags"
                                    content={
                                        <Typeahead
                                            multiple
                                            allowNew
                                            id="decorationTechniquesTags"
                                            onChange={selected => {
                                                handleChange('decorationTechniquesTags', selected.map(x => x.label ? x.label : x))
                                                decorationTechniquesTagsRef.current.toggleMenu()
                                            }}
                                            onBlur={save}
                                            options={decorationTags}
                                            placeholder="Select general decoration techniques"
                                            ref={decorationTechniquesTagsRef}
                                            defaultSelected={craftRecord.decorationTechniquesTags
                                                ? craftRecord.decorationTechniquesTags.split(", ")
                                                : []
                                            }
                                        />
                                    }
                                />
                                <FormRow
                                    key="decorationTechniquesDesc"
                                    label="Specific techniques"
                                    content={
                                        <Form.Control
                                            name="decorationTechniquesDesc"
                                            placeholder="Describe the specific techniques related to the decoration of the craft activity"
                                            defaultValue={craftRecord.decorationTechniquesDesc}
                                            as="textarea"
                                            rows="5"
                                            onChange={e => handleChange(e.target.name, e.target.value)}
                                            onBlur={save}
                                        />
                                    }
                                />
                                <FormRow
                                    key="decorationTechniquesImage"
                                    label="Image of decoration"
                                    hint={<>Accepted types: JPG or PNG<br/>Recommended size: 1200x800</>}
                                    content={
                                        <div className="mb-3">
                                            <ReactImagePickerEditor
                                                config={{
                                                    borderRadius: '8px',
                                                    language: 'en',
                                                    width: '330px',
                                                    height: '250px',
                                                    objectFit: 'contain',
                                                    compressInitial: null,
                                                }}
                                                imageSrcProp={craftRecord.decorationTechniquesImage
                                                    ? `${filesURL}/${craftRecord.decorationTechniquesImage}`
                                                    : null
                                                }
                                                imageChanged={dataUri => {
                                                    handleChange('decorationTechniquesImage', {
                                                        uri: dataUri
                                                    })
                                                    save({
                                                        uri: dataUri
                                                    })
                                                }}
                                            />
                                        </div>
                                    }
                                />
                                <h3>Manufacture phases</h3>
                                <p>Add as many phases as needed for describing the manufacturing process</p>
                                {manufacturingPhases.map((text, index) => (
                                    <FormRow
                                        key={`manufacturingPhase${index+1}`}
                                        label={`Phase ${index + 1}`}
                                        content={
                                            <>
                                                <Form.Control
                                                    name={`manufacturingPhase${index+1}`}
                                                    defaultValue={text}
                                                    as="textarea"
                                                    rows="5"
                                                    onChange={e => handleChangePhase(index, e.target.value)}
                                                    onBlur={save}
                                                />
                                                {index === manufacturingPhases.length - 1 &&
                                                    <Button variant="link" className="p-0" onClick={removeManufacturingPhase}>
                                                        Remove
                                                    </Button>
                                                }
                                            </>
                                        }
                                    />
                                ))}
                                <div className="d-flex mt-2">
                                    <Button variant="outline-primary" className="mb-3" onClick={addManufacturingPhase}>
                                        <FaPlus /> Add phase
                                    </Button>
                                </div>
                                {submitError && 
                                    <Alert key='submitError' variant='danger'>
                                        {submitError}
                                    </Alert>
                                }
                            </>
                        }
                    </>
                }
                {loadError &&
                    <>
                        <Alert key='loadError' variant='danger'>
                            {loadError}
                        </Alert>
                    </>
                }
                <StickyFooter>
                    <Button size="lg" variant="outline-primary" className="float-start" onClick={previousPage}>
                        <FaAngleLeft /> Previous
                    </Button>
                    <Button size="lg" variant="primary" className="float-end" onClick={nextPage}>
                        Next <FaAngleRight />
                    </Button>
                </StickyFooter>
            </main>
        </>
    )
}

export default CraftRecord6